import { FieldArray, FormikProps, useFormikContext } from 'formik'
import {
  ButtonV2,
  DatePickerV2,
  InputStartAdornment,
  LabraToolTipV2,
  PlanType,
  RadioButtonGroupMapping,
  RadioButtonsV2,
  TextInputV4,
  labraUIDateFormat,
} from '@labrav/react-components'
import { get, isUndefined } from 'common/utils/vanilla'
import { useStyles } from './MicrosoftInstallments.styles'
import { Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTrash } from '@fortawesome/pro-regular-svg-icons'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { getRandom } from '../../../../common/utils/helperFunctions'
import clsx from 'clsx'

import { getScheduleTypeOptions } from '../../../../flyout/utils/productCreationUtil'
import {
  AzureOfferPlan,
  ScheduleType,
} from '../../../modules/privateOffer/reducer'
import { Installments } from '../../../../flyout/modules/privateOffer/reducer'
import { DateTime } from 'luxon'
import { getInstallmentsBasedOnSchedule } from '../../../utils/resaleAuthorizationUtil'

interface MicrosoftInstallmentsProps {
  installments?: Installments[]
  tcv?: number
  plan: AzureOfferPlan
  index: number
  durationInMonths: number
  offerStartDate?: string
  handleChangeInstallmentsAmount?: (installments: Installments[]) => void
}

export const MicrosoftInstallments = ({
  tcv,
  installments,
  plan,
  index,
  durationInMonths,
  offerStartDate,
  handleChangeInstallmentsAmount,
}: MicrosoftInstallmentsProps) => {
  const { classes } = useStyles()
  const {
    values,
    setFieldValue,
    setFieldTouched,
    touched,
    handleChange,
    handleBlur,
    errors,
  } = useFormikContext() as FormikProps<Record<string, unknown>>
  const emptyResourse = {
    pricePerPaymentInUsd: '',
    chargeDate: '',
    isImmediateInstallment: false,
  }
  const paymentSchedule =
    plan?.pricing?.recurrentPrice?.prices[0]?.installments?.frequency

  const [randomKey, setRandomKey] = useState<number>(getRandom())

  const scheduleTypeOptions: RadioButtonGroupMapping[] =
    getScheduleTypeOptions(false)

  const checkedValue = useMemo(
    () =>
      paymentSchedule === ScheduleType.QUARTERLY
        ? 'Quarterly'
        : paymentSchedule === ScheduleType.MONTHLY
          ? 'Monthly'
          : paymentSchedule === ScheduleType.YEARLY
            ? 'Yearly'
            : 'Custom',
    [paymentSchedule]
  )

  const handleSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedOptionKey = (event.target as HTMLInputElement).value as string
    const updateField = `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.frequency`
    if (selectedOptionKey === 'Quarterly') {
      setFieldValue(updateField, ScheduleType.QUARTERLY)
      const installments = getInstallmentsBasedOnSchedule({
        tcv: tcv || 0,
        durationInMonths: durationInMonths || 0,
        paymentSchedule: ScheduleType.QUARTERLY,
        now: offerStartDate || '',
      })
      setFieldValue(
        `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule`,
        installments
      )
    } else if (selectedOptionKey === 'Monthly') {
      setFieldValue(updateField, ScheduleType.MONTHLY)
      const installments = getInstallmentsBasedOnSchedule({
        tcv: tcv || 0,
        durationInMonths: durationInMonths || 0,
        paymentSchedule: ScheduleType.MONTHLY,
        now: offerStartDate || '',
      })
      setFieldValue(
        `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule`,
        installments
      )
    } else if (selectedOptionKey === 'Yearly') {
      setFieldValue(updateField, ScheduleType.YEARLY)
      const installments = getInstallmentsBasedOnSchedule({
        tcv: tcv || 0,
        durationInMonths: durationInMonths || 0,
        paymentSchedule: ScheduleType.YEARLY,
        now: offerStartDate || '',
      })
      setFieldValue(
        `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule`,
        installments
      )
    } else if (selectedOptionKey === 'Custom') {
      setFieldValue(updateField, ScheduleType.CUSTOM)
      const installments = getInstallmentsBasedOnSchedule({
        tcv: tcv || 0,
        durationInMonths: durationInMonths || 0,
        paymentSchedule: ScheduleType.CUSTOM,
        now: offerStartDate || '',
      })
      setFieldValue(
        `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule`,
        installments
      )
    }
  }

  const handleChangeAmount = useCallback(
    async (
      event: React.ChangeEvent<HTMLInputElement>,
      indexSchedule: number
    ) => {
      const value = (event.target as HTMLInputElement).value
      const updateField = `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule[${indexSchedule}].pricePerPaymentInUsd`
      await setFieldValue(updateField, value !== '' ? Number(value) : value)
      if (handleChangeInstallmentsAmount && installments) {
        const updatedInstallments = installments
        updatedInstallments[indexSchedule].pricePerPaymentInUsd =
          value !== '' ? Number(value) : null
        await handleChangeInstallmentsAmount(updatedInstallments)
      }
    },
    [setFieldValue, installments, handleChangeInstallmentsAmount]
  )

  const showDelete =
    (installments || []).length > 2 && paymentSchedule === ScheduleType.CUSTOM
  return (
    <div
      className={classes.installmentsWrapper}
      data-testid={'installment-section'}
    >
      <div className={classes.sectionHeading}>Installment schedule</div>
      <div className={classes.radioSelection}>
        <RadioButtonsV2
          checkedValue={{
            key: checkedValue,
            value: checkedValue,
          }}
          name="installments.flexiblePaymentType"
          radioButtonMappings={scheduleTypeOptions}
          handleChange={handleSelection}
          gapBetween="20px"
        />
      </div>
      <div className={clsx(classes.root, classes.elementsGap)}>
        <div className={classes.titleWraper}>
          <Typography className={clsx(classes.sectionText)}>
            Installment amount
          </Typography>
          <Typography className={clsx(classes.sectionText)}>
            Invoice date
          </Typography>
          {showDelete && <div style={{ width: '2%' }}></div>}
        </div>
        <div className={classes.border}></div>
        <FieldArray
          name={`plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule`}
          key="installments"
        >
          {({ push, remove }) => (
            <>
              {(installments || []).map((installment, installmentsIndex) => {
                const handleDateChange = async (date: string | null) => {
                  const updatedDate = date
                    ? (DateTime.fromISO(date).toISO() || '').split('T')[0]
                    : ''

                  await setFieldValue(
                    `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule.${installmentsIndex}.chargeDate`,
                    updatedDate
                  )
                  await setFieldTouched(
                    `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule.${installmentsIndex}.chargeDate`,
                    true
                  )
                }

                return (
                  <>
                    <div
                      className={classes.resourceRow}
                      data-testid="installments-row"
                    >
                      <div className={classes.resourcesContainer}>
                        <div
                          className={classes.input}
                          key={`installments-${randomKey}-${installmentsIndex}-amount`}
                        >
                          <TextInputV4
                            keyName={`plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule[${installmentsIndex}].pricePerPaymentInUsd`}
                            title="Installment amount"
                            type="number"
                            placeHolder=""
                            startAdornment={<InputStartAdornment text="$" />}
                            alignRight
                            value={installment?.pricePerPaymentInUsd as number}
                            updatedValue={
                              installment?.pricePerPaymentInUsd as number
                            }
                            isError={
                              !!(
                                get(
                                  touched,
                                  `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule[${installmentsIndex}].pricePerPaymentInUsd`,
                                  false
                                ) &&
                                get(
                                  errors,
                                  `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule[${installmentsIndex}].pricePerPaymentInUsd`,
                                  false
                                )
                              )
                            }
                            errorText={
                              (!!get(
                                touched,
                                `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule[${installmentsIndex}].pricePerPaymentInUsd`,
                                false
                              ) &&
                                get(
                                  errors,
                                  `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule[${installmentsIndex}].pricePerPaymentInUsd`,
                                  ''
                                )) ||
                              ''
                            }
                            onChange={event =>
                              handleChangeAmount(event, installmentsIndex)
                            }
                            isReadOnly={false}
                            isRequired={false}
                            showSpinner={false}
                            onBlur={handleBlur}
                            keyToPrevent={['+', '-', 'e']}
                          />
                        </div>
                        <div
                          className={classes.input}
                          key={`installments-${randomKey}-${installmentsIndex}-chargeDate`}
                        >
                          <LabraToolTipV2
                            tooltipPlacement="bottom"
                            description={
                              installmentsIndex === 0 && offerStartDate
                                ? 'Contract start date'
                                : ''
                            }
                          >
                            {installment?.isImmediateInstallment ||
                            installmentsIndex === 0 ? (
                              <TextInputV4
                                keyName={`plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule.${installmentsIndex}.chargeDate`}
                                title={'Invoice date'}
                                placeHolder={'Invoice date'}
                                value={'Charge immediately when contact starts'}
                                isReadOnly={true}
                                isError={false}
                                errorText={undefined}
                                onChange={() => void 0}
                                isRequired={false}
                                showSpinner={false}
                                onBlur={handleBlur}
                                keyToPrevent={['+', '-', 'e']}
                              />
                            ) : (
                              <DatePickerV2
                                format={labraUIDateFormat}
                                views={['year', 'month', 'day']}
                                size="small"
                                field={{
                                  title: 'Invoice date',
                                  humanReadableKey: 'Invoice date',
                                  placeHolder: 'Invoice date',
                                  id: `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule.${installmentsIndex}.chargeDate`,
                                  isRequired: false,
                                  isReadOnly:
                                    installment?.isImmediateInstallment ||
                                    installmentsIndex === 0,
                                  isCustom: false,
                                  defaultValue: installment.chargeDate,
                                  dataType: 'date',
                                  minDate: 'today',
                                }}
                                customClasses={
                                  installment?.isImmediateInstallment ||
                                  installmentsIndex === 0
                                    ? classes.disableDate
                                    : ''
                                }
                                fieldId={`plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule.${installmentsIndex}.chargeDate`}
                                handleDateChange={handleDateChange}
                                showErrorHelper={
                                  !!(
                                    !!get(
                                      touched,
                                      `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule.${installmentsIndex}.chargeDate`,
                                      false
                                    ) &&
                                    get(
                                      errors,
                                      `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule.${installmentsIndex}.chargeDate`,
                                      ''
                                    )
                                  )
                                }
                                errorHelperText={
                                  (!!get(
                                    touched,
                                    `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule.${installmentsIndex}.chargeDate`,
                                    false
                                  ) &&
                                    get(
                                      errors,
                                      `plans.${[index]}.pricing.recurrentPrice.prices[0].installments.schedule.${installmentsIndex}.chargeDate`,
                                      ''
                                    )) ||
                                  ''
                                }
                              />
                            )}
                          </LabraToolTipV2>
                        </div>
                      </div>
                      {showDelete ? (
                        <div className={classes.trashIcon}>
                          {installmentsIndex === 0 ? (
                            <div style={{ width: '2%' }}></div>
                          ) : (
                            <FontAwesomeIcon
                              icon={faTrash}
                              style={{
                                width: '14px',
                                height: '20px',
                                color: '#6A777A',
                              }}
                              data-testid="trash-installments"
                              onClick={() => remove(installmentsIndex)}
                            />
                          )}
                        </div>
                      ) : (
                        <></>
                      )}
                    </div>
                  </>
                )
              })}
              {paymentSchedule === ScheduleType.CUSTOM &&
              (installments || [])?.length <= 66 ? (
                <div>
                  <ButtonV2
                    className={classes.widthButton}
                    styleType="link"
                    onClick={() => {
                      push(emptyResourse)
                    }}
                    data-testid="add-a-installments-button"
                  >
                    Add more installments
                  </ButtonV2>
                  <Typography className={classes.buttonSubText}>
                    You can add {66 - (installments || [])?.length} more
                    installments.
                  </Typography>
                </div>
              ) : (
                <></>
              )}
            </>
          )}
        </FieldArray>
      </div>
    </div>
  )
}
